import { useReactiveVar } from '@apollo/client';

import {
  CurrentCustomerAdminFragment,
  CurrentTenantAdminFragment,
} from '@/types/graphql';
import { currentAdminVar, currentAgencyVar } from '@/util/apollo/cache';
import { isClientAdminUser, isTenantAdminUser } from '@/util/adminRole';

// Typeguard to ensure `customer` is a property of the currentAdmin object.
// Defined at module scope, not inside the hook: it closes over nothing, and a
// fresh identity per render defeats every useMemo/useEffect that lists it as a
// dependency. See GRAVY-10366.
const isCurrentCustomerAdmin = (
  arg: CurrentCustomerAdminFragment | CurrentTenantAdminFragment | null,
): arg is CurrentCustomerAdminFragment =>
  (arg as CurrentCustomerAdminFragment).customer !== undefined;

const useAuth = () => {
  const currentAgency = useReactiveVar(currentAgencyVar);

  const currentAdmin = useReactiveVar(currentAdminVar);
  const currentAdminIsCustomerAdmin = isClientAdminUser(currentAdmin);
  // Strict tenant-admin check (helpdesk admins excluded too) — gates
  // GravyWork-internal cost signals like the OT risk banner and the
  // "Hours This Week" staffing column. Keyed on `user.roleId`, what graphy's
  // `require_tenant_admin_role` checks; see util/adminRole for why `role`
  // (graphy's TenantRole level) must not be used.
  const currentAdminIsTenantAdmin = isTenantAdminUser(currentAdmin);
  // Same check; kept as a separate name for existing callers.
  const currentAdminIsTenantAdminRole = currentAdminIsTenantAdmin;

  return {
    currentAdmin,
    currentAdminIsCustomerAdmin,
    currentAdminIsTenantAdmin,
    currentAdminIsTenantAdminRole,
    isCurrentCustomerAdmin,
    currentAgency,
  };
};

export default useAuth;
